import { faCheck, faMinus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Key } from 'antd/es/table/interface';
import { ColumnProps, TablePaginationConfig } from 'antd/lib/table';
import { SorterResult, SortOrder } from 'antd/lib/table/interface';
import { GetComponentProps } from 'rc-table/lib/interface';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton, Table, Tooltip, useMe, UserName } from 'src/common';
import { useFetchMore } from 'src/common/infinityScroll';
import { FeatureFlag, useFeatureFlag } from 'src/helpers/featureFlag';
import EmployeeStatus, { EmployeeStatusBadge } from 'src/pages/SettingsPage/Company/EmployeesSettings/EmployeeStatus';
import { LoadMoreButton } from 'src/shared';
import { isAdminUsersSortField, AdminUsersSortField, AdminUsersSortVars } from 'src/models/admin/Users/types';
import { MfaMode } from 'src/models/users/types';
import AddUserModal from '../AddUserModal';
import InviteUserButton from '../CompanyMembersList/InviteUserButton';
import UserEditableInfo from '../EditableTableCell';
import { useAdminUsers, useAdminUsersVars } from 'src/models/admin/Users/hooks';
import LinkToCompanyInfo from '../LinkToCompanyInfo';
import DeleteUser from './DeleteUser';
import styles from './index.module.scss';
import TableHeader from './TableHeader';
import { AdminUser } from 'src/models/admin';
import { AdminUsersProvider } from 'src/models/admin/Users/providers';
import { Ownerships, ownership } from 'src/models/ownership';

interface AdminUsersColumnProps {
  email: string;
}

export const UserList: FC = () => (
  <AdminUsersProvider>
    <UserListContent />
  </AdminUsersProvider>
);

export const UserListContent: FC = () => {
  const { t } = useTranslation();
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const [{ args: searchArgs }, sortUsers] = useAdminUsersVars();
  const sorting = searchArgs.sortBy;
  const [modalVisible, setModalVisible] = useState(false);

  const showModal = useCallback(() => setModalVisible(true), []);
  const hideModal = useCallback(() => setModalVisible(false), []);

  const isDeleteUserFeature = useFeatureFlag(FeatureFlag.Users_DeleteUser);
  const isInviteFeature = ownership.name !== Ownerships.Clira;

  const onChange = useCallback(
    (
      pagination: TablePaginationConfig,
      filters: Record<string, (Key | boolean)[] | null>,
      sorter: SorterResult<AdminUsersColumnProps> | SorterResult<AdminUsersColumnProps>[]
    ): void => {
      const prepareSortData = (sorter: SorterResult<AdminUsersColumnProps>): AdminUsersSortVars | undefined => {
        const { order } = sorter;
        const apiOrder = order === 'ascend' ? 'ASC' : 'DESC';
        const column = sorter.column;

        return column?.key && isAdminUsersSortField(column?.key) && order
          ? { column: column.key, sort: apiOrder }
          : undefined;
      };

      if (Array.isArray(sorter)) {
        console.error('sorter is array. We do not support this');
      } else {
        const sortData = prepareSortData(sorter);
        sortUsers({ sortBy: sortData });
      }
    },
    [sortUsers]
  );

  const { loading, data: usersData, fetchMore, fetchingMore, refetch } = useAdminUsers();
  const { data: meData } = useMe();

  const columns: ColumnProps<AdminUser>[] = useMemo(
    () => [
      {
        title: t('Common.name'),
        dataIndex: 'name',
        fixed: 'left',
        ellipsis: true,
        className: styles.colName,
        sorter: true,
        key: 'user.name',
        sortOrder: mapSortingToSortOrder('user.name', sorting),
        render: (_, record) => {
          return (
            <Tooltip title={record.email}>
              <UserName user={record} className={styles.userName} size={'s'} showTooltip />
            </Tooltip>
          );
        }
      },
      {
        title: t('Common.email'),
        dataIndex: 'email',
        fixed: 'left',
        ellipsis: { showTitle: true },
        className: styles.colEmail,
        sorter: true,
        key: 'user.email',
        sortOrder: mapSortingToSortOrder('user.email', sorting),
        render: (_, record) => {
          return <span title={record.email}>{record.email}</span>;
        }
      },
      {
        title: t('AdminPage.companyId'),
        dataIndex: 'companyId',
        className: styles.wideCol,
        sorter: true,
        key: 'company.id',
        sortOrder: mapSortingToSortOrder('company.id', sorting),
        render: companyId => {
          return <>{companyId ? <LinkToCompanyInfo companyId={companyId} /> : '—'}</>;
        }
      },
      {
        title: t('Common.createdOn'),
        dataIndex: 'createdOn',
        className: styles.colDate,
        sorter: true,
        key: 'user.createdOn',
        sortOrder: mapSortingToSortOrder('user.createdOn', sorting)
      },
      {
        title: t('AdminPage.mainAccount'),
        dataIndex: 'isAdmin',
        className: styles.colIsAdmin,
        render: isAdmin => <FontAwesomeIcon icon={isAdmin ? faCheck : faMinus} />,
        sorter: true,
        key: 'user.isAdmin',
        sortOrder: mapSortingToSortOrder('user.isAdmin', sorting)
      },
      {
        title: '2FA',
        dataIndex: 'mfaMode',
        className: styles.colIsAdmin,
        align: 'center',
        key: 'user.mfaMode',
        render: mfaMode => (mfaMode === MfaMode.TOTP ? t('Common.activated') : <FontAwesomeIcon icon={faMinus} />)
      },
      {
        title: t('Common.status'),
        dataIndex: 'status',
        className: styles.colIsAdmin,
        render: function RenderActiveStatus(_, record) {
          const { enabled, email } = record;
          return (
            <>
              {meData?.email === email ? (
                <EmployeeStatusBadge enabled={enabled} />
              ) : (
                <EmployeeStatus top enabled={enabled} email={email} />
              )}
            </>
          );
        }
      },
      {
        title: t('AdminPage.pricePlan'),
        dataIndex: 'pricePlan',
        className: styles.wideCol,
        sorter: true,
        key: 'company.pricePlan',
        sortOrder: mapSortingToSortOrder('company.pricePlan', sorting),
        render: pricePlan => {
          return <>{pricePlan ? t(`FeatureRole.role_${pricePlan}`) : '—'}</>;
        }
      },
      {
        title: t('AdminPage.salesOffice'),
        dataIndex: 'companyCountry',
        className: styles.wideCol,
        sorter: true,
        key: 'company.country',
        sortOrder: mapSortingToSortOrder('company.country', sorting),
        render: companyCountry => {
          return <>{t(`SalesOffice.${companyCountry}`)}</>;
        }
      },
      {
        title: t('Company.company'),
        dataIndex: 'companyName',
        className: styles.regularCol,
        sorter: true,
        key: 'company.companyName',
        sortOrder: mapSortingToSortOrder('company.companyName', sorting),
        render: (company, record) => {
          return (
            <UserEditableInfo companyId={record.companyId} companyInfo={'companyName'} companyInfoContent={company} />
          );
        }
      },

      {
        title: t('Company.orgNumber'),
        dataIndex: 'orgNumber',
        className: styles.regularCol,
        sorter: true,
        key: 'company.orgNumber',
        sortOrder: mapSortingToSortOrder('company.orgNumber', sorting),
        render: (_, record) => {
          return (
            <UserEditableInfo
              companyId={record.companyId}
              companyInfo={'orgNumber'}
              companyInfoContent={record.companyOrgNumber}
            />
          );
        }
      },
      {
        title: '',
        className: styles.colAction,
        fixed: 'right',
        render: (_, record) => {
          const { email } = record;
          return (
            <div className={styles.actions}>
              {isInviteFeature && <InviteUserButton user={record} />}
              {isDeleteUserFeature && <DeleteUser companyId={record.companyId} email={email} />}
            </div>
          );
        }
      }
    ],
    [t, sorting, meData?.email, isInviteFeature, isDeleteUserFeature]
  );

  const data = usersData?.users || [];

  const total = usersData?.count || 0;
  const hasMore = data.length < total;

  const loadMore = useCallback(
    async (variables: { offset: number }) => {
      return fetchMore({
        variables
      });
    },
    [fetchMore]
  );
  const onLoadMore = useFetchMore({ offset: usersData?.users.length ?? 0 }, loadMore);

  function getRowId(id: string): string {
    return `userRow-${id}`;
  }

  const onRow: GetComponentProps<AdminUser> = useCallback(record => {
    return {
      id: getRowId(record.email)
    };
  }, []);

  return (
    <div ref={setContainer} className={styles.container}>
      <div className={styles.overviewCard}>
        <TableHeader onCreateUser={showModal} />
        {loading ? (
          <Skeleton loading active />
        ) : (
          <Table
            rowKey={'email'}
            // loading={loading}
            columns={columns}
            dataSource={data}
            className={styles.table}
            scroll={{ x: '1500' }}
            onRow={onRow}
            onChange={onChange}
            rowClassName={styles.row}
            footer={() => (
              <LoadMoreButton
                onLoadMore={onLoadMore}
                isLoading={fetchingMore}
                hasMore={hasMore}
                scrollContainer={container}
              />
            )}
          />
        )}
        {modalVisible && <AddUserModal onHide={hideModal} refetch={refetch} />}
      </div>
    </div>
  );
};

function mapSortingToSortOrder(column: AdminUsersSortField, sorting?: AdminUsersSortVars): SortOrder {
  if (!sorting) {
    return null;
  }

  if (sorting.column === column) {
    switch (sorting.sort) {
      case 'ASC': {
        return 'ascend';
      }
      case 'DESC': {
        return 'descend';
      }
    }
  }
  return null;
}
export default UserList;
